import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { formatContactList, sortByKey } from 'utils/utils';
import { ChangeCaseUserData } from 'modules/cases/Helpers/ChangeCaseUserData';
import { getTenantIdFromLS, getTenantSkFromLS } from 'utils/localStorageUtils';
import { updateMessageThread } from 'services/messageServices';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import { useToast } from '../../../../../../context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { generateFullName } from 'utils/generateFullNameUtils';

const AssignFirmUserResolution = ({
  isLeadSpecific,
  selectedMessage,
  loadFirmUserList = () => {},
  firmUserList = [],
  fetchCaseMessageDetails,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      assign_to: [],
      need_resolution: false,
      resolved: false,
    },
  });

  const { addToast } = useToast();
  const [isReady, setIsReady] = useState(false);
  const watchValues = watch();

  const transformAssignToWithLabels = (assignTo = []) => {
    return assignTo.map((assignee) => {
      const matchingUser = firmUserList.find(
        (user) =>
          user.client_cognito_username === assignee.client_cognito_username ||
          user.contact_id === assignee.contact_id ||
          user.firm_user_id === assignee.firm_user_id
      );

      if (matchingUser) {
        return {
          ...matchingUser,
          label: generateFullName(matchingUser),
        };
      } else {
        return {
          ...assignee,
          label: generateFullName(assignee),
        };
      }
    });
  };

  const isDirty = useMemo(() => {
    if (!selectedMessage) return false;
    const transformedAssignTo = transformAssignToWithLabels(selectedMessage?.assign_to || []);
    return (
      JSON.stringify(watchValues?.assign_to || []) !== JSON.stringify(transformedAssignTo || []) ||
      (watchValues?.need_resolution || false) !== (selectedMessage?.resolution_status || false) ||
      (watchValues?.resolved || false) !== (selectedMessage?.resolved_status || false)
    );
  }, [watchValues, selectedMessage]);

  const formatUserList = useCallback(
    (isGroup) => {
      if (isGroup) {
        const formattedList = formatContactList(firmUserList);
        const sortedList = formattedList?.map((item) =>
          item?.code === 'Users' ? { ...item, items: sortByKey(item.items, 'first_name') } : item
        );
        return ChangeCaseUserData(sortedList || []);
      } else {
        const filterDataWithoutGroups = firmUserList?.filter((item) => item?.res_type !== 'user-group') || [];
        return ChangeCaseUserData(sortByKey(filterDataWithoutGroups, 'first_name'));
      }
    },
    [firmUserList]
  );

  const assignToFirmUserItemTemplate = (option) => (
    <>
      {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
      {option?.label}
    </>
  );

  const onSubmit = (formData) => {
    const tenantId = getTenantIdFromLS();
    const tenant_sk = getTenantSkFromLS();
    let tenantSk = tenant_sk ? tenant_sk.replaceAll('#', '%23') : '';

    let sendData = {
      accessible_list: selectedMessage?.accessible_list,
      case_id: selectedMessage?.case_id,
      case_name: selectedMessage?.case_name,
      sk: selectedMessage?.sk,
      is_new: selectedMessage?.is_new ? selectedMessage?.is_new : false,
      is_read: selectedMessage?.is_read,
      subject: selectedMessage?.subject,
      resolution_status: selectedMessage?.resolution_status ? selectedMessage?.resolution_status : formData?.need_resolution,
      resolved_status: formData?.resolved,
      assign_to: formData?.assign_to,
    };

    updateMessageThread(tenantId, tenantSk, sendData, isLeadSpecific ? true : false)
      .then(() => {
        addToast(
          false,
          toastConstant.toasterType.SUCCESS,
          toastConstant.api.SUCCESS,
          toastConstant.message.SECURE_MESSAGE_THREAD_UPDATED_SUCCESS
        );
        fetchCaseMessageDetails();
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.SECURE_MESSAGE_THREAD_UPDATED_FAILED
        );
      });
  };

  const handleCancel = () => {
    const transformedAssignTo = transformAssignToWithLabels(selectedMessage?.assign_to || []);
    reset({
      assign_to: transformedAssignTo || [],
      need_resolution: selectedMessage?.resolution_status || false,
      resolved: selectedMessage?.resolved_status || false,
    });
  };

  useEffect(() => {
    if (typeof loadFirmUserList === 'function') {
      loadFirmUserList();
    }
  }, []);

  useEffect(() => {
    if (selectedMessage) {
      const transformedAssignTo = transformAssignToWithLabels(selectedMessage?.assign_to || []);

      setValue('assign_to', transformedAssignTo);
      setValue('need_resolution', selectedMessage?.resolution_status || false);
      setValue('resolved', selectedMessage?.resolved_status || false);
      setTimeout(() => {
        setIsReady(true);
      }, 1000);
    }
  }, [selectedMessage, firmUserList, setValue]);

  useEffect(() => {
    const dropdownClasses = document?.querySelectorAll('.p-multiselect-token');
    for (const dropdownElement of dropdownClasses) {
      const dropLabelElement = dropdownElement?.querySelector('.p-multiselect-token-label')?.innerHTML;

      if (dropLabelElement?.trim()?.length === 0) {
        dropdownElement.style.display = 'none';
      }
    }
  }, [selectedMessage, firmUserList]);

  if (!isReady) return null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-start filter-wrap flex-wrap px-3 py-2">
      <label className="me-2 p-medium text-muted mb-0 assign-label-msg" style={{ marginTop: '8px' }}>
        Assign to Firm User {(watch('need_resolution') || watch('resolved')) && <span className="text-danger">*</span>}
      </label>

      <div className="me-3 d-flex flex-column filter-wraper">
        <Controller
          name="assign_to"
          control={control}
          rules={{
            validate: (value) => {
              if ((watch('need_resolution') || watch('resolved')) && (!value || value.length === 0)) {
                return 'Assign to is Required';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <MultiSelect
              inputRef={field.ref}
              value={field.value}
              options={firmUserList ? formatUserList() : []}
              className="input-shadow w-100"
              filter
              placeholder="Select a Firm User"
              onChange={(e) => field.onChange(e.value)}
              optionValue={(v) => v}
              optionLabel="label"
              optionGroupChildren="items"
              display="chip"
              resetFilterOnHide
              emptyFilterMessage="No records found"
              itemTemplate={assignToFirmUserItemTemplate}
            />
          )}
        />
        {errors?.assign_to && <InputErrorMessage className="mt-1">{errors.assign_to.message}</InputErrorMessage>}
      </div>
      {!selectedMessage?.resolution_status ? (
        <>
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center">
              <label className="me-2 p-medium text-muted mb-0" style={{ marginTop: '8px' }}>
                Need Resolution
              </label>

              <Controller
                name="need_resolution"
                control={control}
                render={({ field }) => (
                  <InputSwitch
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);

                      if (!e.value) {
                        clearErrors('assign_to');
                      }
                    }}
                    className="input-swich me-2"
                    style={{ marginTop: '6px' }}
                  />
                )}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center">
              <label className="me-2 p-medium text-muted mb-0" style={{ marginTop: '8px' }}>
                Resolved
              </label>
              <Controller
                name="resolved"
                control={control}
                render={({ field }) => (
                  <InputSwitch
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                      if (!e.value) {
                        clearErrors('assign_to');
                      }
                    }}
                    className="input-swich me-2"
                    style={{ marginTop: '6px' }}
                  />
                )}
              />
            </div>
          </div>
        </>
      )}

      {(isDirty || Object.keys(errors).length > 0) && (
        <>
          <Button
            label="Cancel"
            className="btn-outline p-button-secondary outline py-1 px-3 me-2 mt-1"
            type="button"
            style={{ overflow: 'inherit' }}
            onClick={handleCancel}
          />
          <Button label="Save" className="btn-outline p-button-primary p-button-secondary py-1 ps-3 mt-1" type="submit" />
        </>
      )}
    </form>
  );
};

export default AssignFirmUserResolution;
